import Template from '../../component/Template.js';
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from 'react';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../../component/Api.js';

import React, { useRef } from "react";
import PDFModal from '../../component/PDFModal.js';

import 'datatables.net';
import 'datatables.net-bs4/css/dataTables.bootstrap4.min.css';


export default function IMUUserCreate({ onClose }) {

  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/ino/vendorlist');
    }
  };

  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    vendorName: '',
    vendorType: '',
    phoneNumber: '',
    email: '',
    address: '',
    description: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [existingFile, setExistingFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { http } = Api();

  const validateForm = () => {

    const newErrors = {};

    if (!formData.vendorName.trim()) {
      newErrors.vendorName = 'Vendor name is required';
    } else if (formData.vendorName.trim().length < 3) {
      newErrors.vendorName = 'Vendor name must be at least 3 characters';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[0-9]\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Enter a valid 10-digit phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !fileError;
  };

  useEffect(() => {

    if (isEdit) {
      loadTopicDetails();
    }

  }, [id]);

  const loadTopicDetails = async () => {

    try {

      const res = await http.get(`College/vendor_details`, {
        params: {
          id: id
        }
      });

      const data = res.data.data;

      setFormData({

        vendorName: data.vendor_name,
        phoneNumber: data.vendor_mobileno,
        email: data.vendor_email,
        address: data.vendor_address,
        description: data.description || '',

      });

      const document = res.data.documents?.[0];


      if (document?.encryptedFile) {

        setExistingFile({
          encryptedFile: document.encryptedFile,
          name: document.docu_name || 'Document',
          type: 'pdf'
        });

      }

    } catch (err) {
      console.error(err);
      toast.error("Failed to load topic details");
    }
  };


  const handleInputChange = (e) => {

    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {

      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));

    }
  };

  const handleFileChange = (e) => {

    const file = e.target.files[0];
    setFileError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Check if file is PDF
    if (file.type !== 'application/pdf') {

      setFileError('Only PDF files are allowed');
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;

    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {

      setFileError('File size must be less than 5MB');

      setSelectedFile(null);
      if (fileInputRef.current) {

        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    toast.success('File uploaded successfully');
  };

  const removeFile = () => {

    setSelectedFile(null);

    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

  };


  const handleViewExistingFile = async () => {

    if (existingFile && existingFile.encryptedFile) {
      try {

        const res = await http.get(`College/secure_ino_pdf/${existingFile.encryptedFile}`, {
          responseType: 'blob' // Important: Get response as blob
        });

        // Create blob URL from the PDF response
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setIsPDFModalOpen(true);

      } catch (error) {

        console.error('Error loading PDF:', error);
        toast.error('Failed to load PDF document');
      }
    }
  };

  const handleClosePDFModal = () => {

    setIsPDFModalOpen(false);
    // Clean up the blob URL to prevent memory leaks
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    // // 🚨 File validation
    // if (!isEdit && !selectedFile) {
    //   toast.error('Please select a file before submitting');
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('vendor_name', formData.vendorName);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('description', formData.description);

      // Append file if selected
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }

      // Append delete flag if existing file was deleted
      if (isEdit && existingFile === null && loadTopicDetails) {
        formDataToSend.append('delete_existing_file', 'true');
      }

      const apiUrl = isEdit
        ? `College/updateVendor/${id}`
        : `College/addVendor`;

      const response = await http.post(apiUrl, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message || 'Vendor created successfully');

      // Reset form after success and close

      handleClose(); // Close form after successful creation

    } catch (error) {
      if (error.response?.data?.errors) {
        // Laravel validation errors
        setErrors({
          vendorName: error.response.data.errors.vendor_name?.[0],
          phoneNumber: error.response.data.errors.phone_number?.[0],
          email: error.response.data.errors.email?.[0],
          address: error.response.data.errors.address?.[0],
          description: error.response.data.errors.description?.[0],
        });
      }

      toast.error(
        error.response?.data?.message || 'Failed to create vendor'
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };





  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Template>
        <div className="container mb-5">
          <div className="row justify-content-center">
            <div className="col-12 col-md-12 col-lg-8 col-xl-6">
              <div className="card shadow-lg border-0 rounded-4">
                <div className="card-body p-4 p-md-5">
                  {/* Header Section */}
                  <div className="text-center mb-4">
                    <div
                      className="d-inline-flex align-items-center justify-content-center  bg-primary rounded-circle mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <i className="bi bi-person-plus-fill fs-2 text-white"></i>
                    </div>

                    <h2 className="fw-bold mb-2">

                      {isEdit ? "Edit Internship Providing Organisation" : "Create New Internship Providing Organisation"} </h2>
                  </div>


                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    {/* Vendor Name Input */}
                    <div className="mb-4">
                      <label htmlFor="vendorName" className="form-label fw-semibold">
                        IPO Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control form-control-lg ${errors.vendorName ? 'is-invalid' : ''}`}
                        id="vendorName"
                        name="vendorName"
                        value={formData.vendorName}
                        onChange={handleInputChange}
                        placeholder="Enter IPO name"
                        disabled={isSubmitting}
                      />
                      {errors.vendorName && (
                        <div className="invalid-feedback">
                          {errors.vendorName}
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        IPO - Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={11}
                        name="phoneNumber"
                        className={`form-control form-control-lg ${errors.phoneNumber ? 'is-invalid' : ''
                          }`}
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Enter IPO phone number"
                        disabled={isSubmitting}
                      />
                      {errors.phoneNumber && (
                        <div className="invalid-feedback">{errors.phoneNumber}</div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        IPO - Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''
                          }`}
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter IPO email address"
                        disabled={isSubmitting}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        IPO - Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        name="address"
                        rows="3"
                        className={`form-control ${errors.address ? 'is-invalid' : ''
                          }`}
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter IPO address"
                        disabled={isSubmitting}
                      />
                      {errors.address && (
                        <div className="invalid-feedback">{errors.address}</div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label htmlFor="description" className="form-label fw-semibold">
                        IPO - Description
                      </label>
                      <textarea
                        name="description"
                        rows="4"
                        className={`form-control ${errors.description ? 'is-invalid' : ''
                          }`}
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter IPOdescription"
                        disabled={isSubmitting}
                      />
                      {errors.description && (
                        <div className="invalid-feedback">{errors.description}</div>
                      )}
                    </div>

                    {/* File Upload */}
                    <div className="mb-4">
                      <label htmlFor="pdfFile" className="form-label fw-semibold">
                        {/* Upload MOU  */}
                        Upload MoU/Consent Letter<span className="text-muted">(Max 5MB)</span>
                      </label>

                      {/* Show existing file in edit mode */}
                      {isEdit && existingFile && !selectedFile && (
                        <div className="alert alert-light d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-file-earmark-pdf fs-4 text-danger me-3"></i>
                            <div>
                              <div className="fw-semibold">{existingFile.name}</div>
                              <div className="text-muted small">Existing PDF document</div>
                            </div>
                          </div>
                          <div>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm me-2"
                              onClick={handleViewExistingFile}
                              disabled={isSubmitting}
                            >
                              <i className="bi bi-eye me-1"></i>
                              View
                            </button>

                          </div>
                        </div>
                      )}

                      <div className="border-2 border-dashed border-secondary rounded-3 p-4 text-center bg-light">
                        <input
                          ref={fileInputRef}
                          type="file"
                          id="pdfFile"
                          accept=".pdf,application/pdf"
                          onChange={handleFileChange}
                          className="d-none"
                          disabled={isSubmitting}
                        />

                        {!selectedFile ? (
                          <div>
                            <i className="bi bi-file-earmark-arrow-up fs-1 text-muted mb-3 d-block"></i>
                            <label htmlFor="pdfFile" className="btn btn-outline-primary cursor-pointer">
                              <i className="bi bi-cloud-upload me-2"></i>
                              Choose File
                            </label>
                            <p className="text-muted small mt-2 mb-0">
                              Only PDF files are allowed. Maximum file size: 5MB
                            </p>
                            {isEdit && existingFile && (
                              <p className="text-info small mt-2 mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Upload a new file to replace the existing document
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-file-earmark-pdf fs-4 text-danger me-3"></i>
                              <div className="text-start">
                                <div className="fw-semibold text-black">{selectedFile.name}</div>
                                <div className="text-muted small">
                                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • PDF
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={removeFile}
                              disabled={isSubmitting}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      {fileError && (
                        <div className="text-danger small mt-2">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {fileError}
                        </div>
                      )}
                    </div>


                    {/* Vendor Type Selection */}
                    {/* <div className="mb-4">
                      <label className="form-label fw-semibold mb-3">
                        IPO Type <span className="text-danger">*</span>
                      </label>
                      <div className="row g-3">
                        
                        <div className="col-6">
                          <input
                            type="radio"
                            className="btn-check"
                            name="vendorType"
                            id="teacher"
                            value="teacher"
                            checked={formData.vendorType === 'teacher'}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                          />
                          <label
                            className={`btn btn-outline-primary w-100 py-3 d-flex flex-column align-items-center ${errors.vendorType && !formData.vendorType ? 'border-danger' : ''}`}
                            htmlFor="teacher"
                          >
                            <i className="bi bi-person-video3 fs-2 mb-2"></i>
                            <span className="fw-semibold">Teacher</span>
                          </label>
                        </div>

                        
                        <div className="col-6">
                          <input
                            type="radio"
                            className="btn-check"
                            name="vendorType"
                            id="vendor"
                            value="vendor"
                            checked={formData.vendorType === 'vendor'}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                          />
                          <label
                            className={`btn btn-outline-primary w-100 py-3 d-flex flex-column align-items-center ${errors.vendorType && !formData.vendorType ? 'border-danger' : ''}`}
                            htmlFor="vendor"
                          >
                            <i className="bi bi-shop fs-2 mb-2"></i>
                            <span className="fw-semibold">IPO</span>
                          </label>
                        </div>
                      </div>
                      {errors.vendorType && (
                        <div className="text-danger small mt-2">
                          {errors.vendorType}
                        </div>
                      )}
                    </div> */}

                    {/* Action Buttons */}
                    <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4 pt-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : isEdit ? (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Update IPO
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Create IPO
                          </>

                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>



            </div>
          </div>
        </div>
      </Template>
      <PDFModal
        isOpen={isPDFModalOpen}
        onClose={handleClosePDFModal}
        pdfUrl={pdfUrl}
        fileName={existingFile?.name}
      />
    </>
  );
}
